import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Plane, Mic, Type, MapPin, Sparkles, Receipt, Puzzle, Layers } from "lucide-react";
import Seo from "../components/Seo";
import PricingCard from "../components/PricingCard";
import PricingHints from "../components/PricingHints";
import useReveal from "../hooks/useReveal";

const PACKAGES = [
  {
    name: "Premium Film",
    price: 667,
    addonPrice: 597,
    tagline: "Cineastisch. Für besondere Objekte.",
    description:
      "Hochwertiger Immobilienfilm für Neubauprojekte, Bauträger, exklusive Maklerobjekte oder Immobilien mit besonderer Architektur.",
    features: [
      "Ca. 90 – 120 Sekunden Film",
      "Horizontale Hauptversion",
      "Vertikale Social-Version",
      "Individuelle Vorab-Abstimmung",
      "Klare Bild- und Schnittstrategie",
      "Innen-, Außen- und Detailsequenzen",
      "Stärkere dramaturgische Führung",
      "Hochwertige Farbkorrektur inkl. Color Grading",
      "Priorisierte Bearbeitung",
    ],
    deliveryTime: "48 – 72 Stunden",
  },
  {
    name: "Plus Film",
    price: 467,
    addonPrice: 397,
    featured: true,
    badge: "Empfohlen",
    tagline: "Professioneller Objektfilm.",
    description:
      "Optimiert für Web-Exposé, Website und YouTube. Empfohlen für eine stärkere Immobilien\u00advermarktung.",
    features: [
      "Ca. 45 – 75 Sekunden Video",
      "Horizontale Version für Website, Web-Exposé, Portale und YouTube",
      "Innen- und Außenaufnahmen",
      "Hochwertige 4K-Videoaufnahme",
      "Dynamische Kamerafahrten und fließende Übergänge",
      "Detailsequenzen für Atmosphäre und Ausstattung",
      "Individueller und sauberer Schnitt",
      "Professionelle Farbkorrektur",
    ],
    deliveryTime: "48 – 72 Stunden",
  },
  {
    name: "Basis Video",
    price: 267,
    addonPrice: 197,
    tagline: "Reel für Social Media.",
    description:
      "Für die schnelle digitale Objektpräsentation auf Instagram, TikTok und Facebook.",
    features: [
      "Ca. 20 – 30 Sekunden Video",
      "Vertikales Format für Instagram, TikTok, Facebook und Shorts",
      "Schnelle, ansprechende Schnitte",
      "Ausgewählte Raum- und Detailaufnahmen",
    ],
    deliveryTime: "24 – 48 Stunden",
  },
];

const EXTRAS = [
  {
    icon: Plane,
    title: "Drohnenaufnahmen",
    subtitle:
      "Für zusätzliche Außenperspektiven, Grundstücksaufnahmen und eine hochwertigere Gesamtwirkung der Immobilie.",
    price: "77 €",
  },
  {
    icon: Mic,
    title: "Kameraauftritt + Untertitel",
    subtitle:
      "Ideal für Makler, Eigentümer oder Projektverantwortliche, die das Objekt persönlich vorstellen möchten. Inklusive Untertiteln für eine professionelle und barriereärmere Darstellung auf Social Media und Web-Plattformen.",
    price: "97 €",
  },
  {
    icon: Type,
    title: "Texteinblendungen",
    subtitle:
      "Zum Beispiel für Wohnfläche, Zimmeranzahl, Lage, Highlights oder besondere Ausstattungsmerkmale.",
    price: "37 €",
  },
];

export default function PricingVideo() {
  useReveal();
  useEffect(() => { window.scrollTo({ top: 0 }); }, []);

  return (
    <main data-testid="pricing-video-page" className="bg-[#F8F5EF]">
      <Seo
        title="Immobilienvideografie Preise"
        description="Transparente Preispakete für professionelle Immobilienvideografie in NRW. Reels, Objektfilme und Premium-Filme ab 267 € zzgl. MwSt."
        path="/leistungen-preise/immobilienvideografie-preise"
      />

      {/* Hero */}
      <section className="pt-32 md:pt-40 pb-10 md:pb-14 border-b border-[#EAE6DE]">
        <div className="container-x">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end reveal">
            <div className="lg:col-span-8">
              <div className="overline mb-5">Leistungen & Preise</div>
              <h1 className="font-display text-5xl md:text-6xl lg:text-[76px] leading-[1.02] text-[#1A1A1A] tracking-tight font-light">
                Immobilien&shy;videografie <br />
                <span className="italic gold-text">Preise</span>.
              </h1>
              <p className="mt-6 max-w-2xl text-[15px] md:text-[17px] leading-[1.6] text-[#3A3A3A] font-light">
                Immobilienvideos, die Aufmerksamkeit erzeugen und Raumgefühl vermitteln — für Social
                Media, Web-Exposés, Websites, YouTube und Portale.
              </p>
            </div>
            <div className="lg:col-span-4 lg:justify-self-end">
              <Link
                to="/#kontakt"
                data-testid="pricing-video-hero-cta"
                className="group inline-flex items-center justify-between px-8 py-4 bg-[#1A1A1A] text-[#F8F5EF] text-xs uppercase tracking-[0.2em] hover:bg-[#FFA500] hover:text-[#1A1A1A] transition-colors duration-300 min-w-[280px] font-medium"
              >
                Jetzt Angebot anfordern
                <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:rotate-45" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Packages */}
      <section className="pt-16 md:pt-24 pb-24 md:pb-32">
        <div className="container-x">
          <div className="mb-12 md:mb-16 reveal">
            <div className="overline mb-4">Videopakete</div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] text-[#1A1A1A] tracking-tight">
              Vom Social-Reel <br />
              zum <span className="italic gold-text">cineastischen Film</span>.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 items-stretch">
            {PACKAGES.map((p) => (
              <PricingCard key={p.name} {...p} />
            ))}
          </div>

          <div className="mt-12 md:mt-16 flex justify-center reveal">
            <Link
              to="/#kontakt"
              data-testid="pricing-video-packages-cta"
              className="group inline-flex items-center justify-between px-10 py-5 bg-[#1A1A1A] text-[#F8F5EF] text-xs uppercase tracking-[0.2em] hover:bg-[#FFA500] hover:text-[#1A1A1A] transition-colors duration-300 min-w-[320px] font-medium"
            >
              Jetzt Angebot anfordern
              <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:rotate-45" />
            </Link>
          </div>
        </div>
      </section>

      {/* Optional Extensions */}
      <section className="py-24 md:py-32 bg-[#F5F2EC]/50 border-t border-[#EAE6DE]">
        <div className="container-x">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-16 reveal">
            <div className="md:col-span-7">
              <div className="overline mb-4">Optionale Erweiterungen</div>
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] text-[#1A1A1A] tracking-tight">
                Ergänzen Sie Ihr <br />
                <span className="italic gold-text">Videopaket</span>.
              </h2>
            </div>
            <div className="md:col-span-4 md:col-start-9 md:pt-8">
              <p className="text-[14px] leading-relaxed text-[#5A5A5A] font-light">
                <strong className="text-[#1A1A1A] font-medium">Hinweis:</strong> Nur in Kombination mit
                einem Videopaket buchbar. Add-ons ersetzen kein Hauptpaket.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#EAE6DE] border border-[#EAE6DE]">
            {EXTRAS.map((e, idx) => (
              <div
                key={e.title}
                data-testid={`pricing-video-extra-${idx}`}
                className="bg-[#F8F5EF] p-8 md:p-10 hover:bg-white transition-colors duration-500 reveal flex flex-col"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#FFA500] shrink-0">
                    <e.icon size={20} className="text-[#1A1A1A]" strokeWidth={1.5} />
                  </div>
                  <span className="ticker-num text-[#FFA500] text-3xl whitespace-nowrap">{e.price}</span>
                </div>
                <h3 className="mt-6 font-display text-[22px] md:text-[26px] leading-tight text-[#1A1A1A] tracking-tight">
                  {e.title}
                </h3>
                <p className="mt-3 text-[13px] leading-relaxed text-[#5A5A5A] font-light flex-1">
                  {e.subtitle}
                </p>
                <div className="mt-6 pt-6 border-t border-[#EAE6DE] text-[11px] uppercase tracking-[0.2em] text-[#5A5A5A]">
                  Add-on · zzgl. MwSt.
                </div>
              </div>
            ))}
          </div>

          <p className="mt-12 text-[13px] text-[#5A5A5A] font-light">
            Alle optionalen Erweiterungen sind ausschließlich in Kombination mit einem Videopaket buchbar.
          </p>
        </div>
      </section>

      {/* Hints */}
      <PricingHints
        hints={[
          { icon: MapPin, text: "In Aachen, Düren und Köln fallen keine Fahrkosten an." },
          { icon: Puzzle, text: "Optionale Erweiterungen sind ausschließlich in Verbindung mit einem Videopaket buchbar." },
          { icon: Layers, text: "Add-ons ergänzen die Hauptpakete gezielt und werden klar als zusätzliche Leistungen ausgewiesen." },
          { icon: Sparkles, text: "Die Preise können je nach Projektumfang, Objektgröße, Standort und gewünschter Leistung variieren." },
          { icon: Receipt, text: "Alle Preise verstehen sich zzgl. MwSt." },
        ]}
      />

      {/* Closing CTA */}
      <section className="relative overflow-hidden bg-[#1A1A1A] text-[#F8F5EF]">
        <div className="container-x py-24 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-8 reveal">
            <div className="overline text-[#FFA500] mb-6">Bereit?</div>
            <h2 className="font-display text-4xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight">
              Bereit für eine <br />
              <span className="gold-text italic">aufmerksamkeitsstarke</span> Präsentation?
            </h2>
            <p className="mt-8 max-w-xl text-[15px] leading-relaxed text-[#A8A59E] font-light">
              Fordern Sie jetzt ein individuelles Angebot für Ihre Immobilienvideografie an.
              Antwort innerhalb eines Werktages.
            </p>
          </div>
          <div className="md:col-span-4 md:justify-self-end reveal">
            <Link
              to="/#kontakt"
              data-testid="pricing-video-final-cta"
              className="group inline-flex items-center justify-between px-8 py-4 bg-[#FFA500] text-[#1A1A1A] text-xs uppercase tracking-[0.2em] hover:bg-[#F8F5EF] transition-colors duration-300 min-w-[300px] font-medium"
            >
              Jetzt Angebot anfordern
              <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:rotate-45" />
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
